import React, { useState } from "react";
import { BadgeCheck, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StarRating } from "@/components/common/StarRating";
import { PhotoLightbox } from "@/components/reviews/PhotoLightbox";
import { ReviewReply } from "@/components/reviews/ReviewReply";
import { AdminReviewControls, ReviewStatusChip } from "@/components/reviews/AdminReviewControls";
import { assetUrl } from "@/lib/api";
import { formatDate } from "@/lib/format";

const SORTS = [
  { value: "recent", label: "Most Recent" },
  { value: "highest", label: "Highest Rated" },
  { value: "lowest", label: "Lowest Rated" },
  { value: "photos", label: "With Photos" },
];

export function ReviewCard({ review, onPhoto, admin, header }) {
  const dimmed = admin && review.status !== "published";
  return (
    <article className={`border border-border p-6 ${dimmed ? "bg-stone-50/70" : "bg-white/50"}`} data-testid="review-card" data-status={review.status}>
      {header}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StarRating rating={review.rating} size={13} />
          {admin && <ReviewStatusChip status={review.status} />}
        </div>
        <time className="text-xs text-muted-foreground" dateTime={review.created_at}>{formatDate(review.created_at)}</time>
      </div>
      {review.title && <h3 className="mt-3 font-serif text-xl text-ink" data-testid="review-card-title">{review.title}</h3>}
      <p className="mt-2 text-sm leading-relaxed text-ink/90" data-testid="review-card-body">{review.body}</p>
      {review.photos?.length > 0 && (
        <div className="mt-4 flex gap-2">
          {review.photos.map((p, idx) => (
            <button key={p.id} type="button" onClick={() => onPhoto(review.photos, idx)} data-testid="review-card-photo" className="h-20 w-20 overflow-hidden border border-border transition-opacity hover:opacity-80">
              <img src={assetUrl(p.url)} alt={`Photo by ${review.name}`} loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <span data-testid="review-card-author">— {review.name}</span>
        {review.verified && (
          <span className="inline-flex items-center gap-1 text-gold" data-testid="review-card-verified"><BadgeCheck size={13} /> Verified Buyer</span>
        )}
      </div>
      <ReviewReply reply={review.reply} />
      {admin && <AdminReviewControls review={review} admin={admin} />}
    </article>
  );
}

function ReviewListHeader({ total, ratingFilter, onClearFilter, admin, sort, onSort }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
      <p className="text-sm text-muted-foreground" data-testid="reviews-written-count">
        {total} {total === 1 ? "review" : "reviews"}
        {admin && <span className="ml-1 text-gold" data-testid="reviews-moderation-note">· incl. hidden & pending (admin view)</span>}
        {ratingFilter && (
          <>
            {" "}· {ratingFilter} star{" "}
            <button type="button" onClick={onClearFilter} className="underline underline-offset-4 hover:text-gold" data-testid="reviews-clear-filter">Clear</button>
          </>
        )}
      </p>
      <Select value={sort} onValueChange={onSort}>
        <SelectTrigger className="h-10 w-[170px] rounded-none border-border bg-transparent text-xs uppercase tracking-wider" data-testid="review-sort-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-none">
          {SORTS.map((s) => (
            <SelectItem key={s.value} value={s.value} className="text-xs uppercase tracking-wider">{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ReviewListBody({ items, isLoading, admin, onPhoto }) {
  if (isLoading && !items.length) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-border py-16 text-center" data-testid="reviews-empty">
        <p className="font-serif text-2xl text-ink">No written reviews yet</p>
        <p className="mt-2 text-sm text-muted-foreground">Be the first to share how this piece looks and feels.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
      {items.map((r) => <ReviewCard key={r.id} review={r} onPhoto={onPhoto} admin={admin} />)}
    </div>
  );
}

export function ReviewList({ items = [], total = 0, isLoading, hasMore, onLoadMore, sort, onSort, ratingFilter, onClearFilter, admin }) {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div>
      <ReviewListHeader total={total} ratingFilter={ratingFilter} onClearFilter={onClearFilter} admin={admin} sort={sort} onSort={onSort} />
      <ReviewListBody items={items} isLoading={isLoading} admin={admin} onPhoto={(photos, index) => setLightbox({ photos, index })} />

      {hasMore && (
        <div className="mt-8 text-center">
          <button type="button" onClick={onLoadMore} disabled={isLoading} data-testid="reviews-load-more" className="border border-border px-8 py-3.5 text-[11px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-gold hover:text-gold disabled:opacity-50">
            {isLoading ? "Loading…" : "Load More Reviews"}
          </button>
        </div>
      )}

      <PhotoLightbox photos={lightbox?.photos || []} index={lightbox?.index || 0} open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)} />
    </div>
  );
}
